import { z } from 'zod';

import {
  REPORT_HABITAT_TYPES,
  REPORT_OBSERVATION_TYPES,
  REPORT_REVIEW_STATUSES,
  REPORT_SPECIES,
} from '../../features/reports/reportTaxonomy';
import rawFirebasePlatformFixture from './firebase-platform.json';
import rawReportTaxonomyFixture from './report-taxonomy.json';
import rawSeedContractFixture from './seed-contract.json';

const formatValidationIssues = (error: z.ZodError): string =>
  error.issues
    .map((issue) => {
      const path = issue.path.length > 0 ? issue.path.join('.') : '<root>';
      return `${path}: ${issue.message}`;
    })
    .join('; ');

const EmulatorProfileSchema = z
  .object({
    auth: z.object({ port: z.literal(9099) }).strict(),
    functions: z.object({ port: z.literal(5001) }).strict(),
    firestore: z.object({ port: z.literal(8082) }).strict(),
    storage: z.object({ port: z.literal(9199) }).strict(),
    ui: z.object({ port: z.literal(4000) }).strict(),
  })
  .strict();

const LocalFirebasePlatformProfileSchema = z
  .object({
    projectId: z.literal('demo-evoc'),
    storageBucket: z.literal('demo-evoc.firebasestorage.app'),
    functionsRegion: z.literal('europe-west8'),
    emulators: EmulatorProfileSchema,
  })
  .strict();

const RemoteFirebasePlatformProfileSchema = z
  .object({
    projectId: z.literal('applicazione-evoc'),
    storageBucket: z.literal('applicazione-evoc.firebasestorage.app'),
    functionsRegion: z.literal('europe-west8'),
    passwordPolicyApiKey: z.string().min(1),
  })
  .strict();

const FirebasePlatformFixtureSchema = z
  .object({
    schemaVersion: z.literal(2),
    profiles: z
      .object({
        local: LocalFirebasePlatformProfileSchema,
        remote: RemoteFirebasePlatformProfileSchema,
      })
      .strict(),
    requiredSeedEmulators: z.tuple([
      z.literal('auth'),
      z.literal('firestore'),
      z.literal('storage'),
      z.literal('functions'),
    ]),
  })
  .strict();

const SeedUserSchema = z
  .object({
    handle: z.string().min(1),
    uid: z.string().min(1),
    email: z.string().email(),
    password: z.string().min(1),
    displayName: z.string().min(1),
    roles: z.array(z.enum(['admin', 'annotator'])),
    emailVerified: z.boolean(),
    disabled: z.boolean(),
  })
  .strict();

const SeedBackofficeFilterValueSchema = z.string().min(1);

const SeedCoordinateSchema = z
  .object({
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180),
  })
  .strict();

const SeedGeographyAreaSchema = z.enum(['north', 'center', 'south', 'islands']);

const SeedSightingPlaceSchema = z
  .object({
    key: z.string().min(1),
    locality: z.string().min(1),
    region: z.string().min(1),
    area: SeedGeographyAreaSchema,
    center: SeedCoordinateSchema,
    radiusKm: z.number().positive(),
  })
  .strict();

const SeedGeographySchema = z
  .object({
    places: z.array(SeedSightingPlaceSchema).min(1),
    requiredSpreadAreas: z.array(SeedGeographyAreaSchema).min(1),
    reportPlaceOverrides: z.record(z.string().min(1), z.string().min(1)),
    workflowSmokePlaceKey: z.string().min(1),
  })
  .strict();

const BackofficeMapScenarioSchema = z
  .object({
    reportIds: z.array(z.string().min(1)),
  })
  .strict();

const RejectedPurgeExpectedOutcomesSchema = z
  .object({
    eligible: z.array(z.string().min(1)),
    tooNew: z.array(z.string().min(1)),
    activeRetentionHoldSkipped: z.array(z.string().min(1)),
    expiredRetentionHoldEligible: z.array(z.string().min(1)),
    missingFinalityMigrationDebt: z.array(z.string().min(1)),
    malformedFinalityMigrationDebt: z.array(z.string().min(1)),
    cutoffBoundaryEligible: z.array(z.string().min(1)),
    paginationEligible: z.array(z.string().min(1)),
  })
  .strict();

const RejectedPurgePolicySchema = z
  .object({
    policyVersion: z.literal('rejected-report-purge/v1'),
    policyRevision: z.string().min(1).max(128),
    approvalStatus: z.literal('approved'),
    approvalReference: z.string().min(1).max(128),
    approvedAtMs: z.number().int().nonnegative(),
    rejectedReportRetentionDays: z.number().int().min(1).max(3650),
    invocationRetentionDays: z.number().int().min(1).max(3650),
    applyTechnicalStateRetentionDays: z.number().int().min(1).max(30),
    cutoffTimezone: z.literal('UTC'),
    cutoffBoundary: z.literal('rejectionFinalityAtMs-lte-cutoff'),
    dryRunEnabled: z.boolean(),
    applyEnabled: z.boolean(),
    automaticApplyEnabled: z.boolean(),
    emergencyPaused: z.boolean(),
  })
  .strict();

const RejectedPurgeSchema = z
  .object({
    policy: RejectedPurgePolicySchema,
    pageSize: z.number().int().positive(),
    cutoffBoundaryMs: z.number().int().nonnegative(),
    expectedOutcomes: RejectedPurgeExpectedOutcomesSchema,
    rawMalformedFinality: z
      .object({
        handle: z.string().min(1),
        rawFields: z
          .object({
            reviewStatus: z.literal('rejected'),
            rejectionFinalityAtMs: z.literal('not-a-number'),
          })
          .strict(),
      })
      .strict(),
    auditHistory: z
      .object({
        completedDryRun: z
          .object({
            recordId: z.string().min(1),
            policyRevision: z.string().min(1),
            requestedAtMs: z.number().int().nonnegative(),
            completedAtMs: z.number().int().nonnegative(),
            cutoffMs: z.number().int().nonnegative(),
            operatorAuditReference: z.string().regex(/^[a-f0-9]{64}$/),
            operatorAuditReferenceKeyId: z.string().min(1),
            cumulativeCounts: z
              .object({
                scanned: z.number().int().nonnegative(),
                eligible: z.number().int().nonnegative(),
                activeRetentionHoldSkipped: z.number().int().nonnegative(),
                statusMismatchSkipped: z.number().int().nonnegative(),
                cutoffMismatchSkipped: z.number().int().nonnegative(),
                unsupportedPolicyVersionSkipped: z.number().int().nonnegative(),
              })
              .strict(),
            warningCodes: z.array(z.string().min(1)),
          })
          .strict(),
      })
      .strict(),
  })
  .strict();

const SeedContractSchema = z
  .object({
    schemaVersion: z.literal(1),
    platformProfile: z.literal('local'),
    taxonomyContractVersion: z.literal(1),
    privacyPolicy: z
      .object({
        currentRequirement: z
          .object({
            version: z.string().min(1),
            locale: z.string().min(1),
            displayUrl: z.string().url(),
            mediaType: z.literal('text/html'),
            contentHash: z.string().min(1),
          })
          .strict(),
        acceptedUserHandles: z.array(
          z.enum(['ownerOne', 'ownerTwo', 'admin', 'annotator', 'adminAnnotator', 'nonPrivileged'])
        ),
        acceptedAt: z.string().datetime(),
      })
      .strict(),
    geography: SeedGeographySchema,
    users: z
      .object({
        ownerOne: SeedUserSchema,
        ownerTwo: SeedUserSchema,
        admin: SeedUserSchema,
        annotator: SeedUserSchema,
        adminAnnotator: SeedUserSchema,
        nonPrivileged: SeedUserSchema,
      })
      .strict(),
    reports: z
      .object({
        app: z
          .object({
            infiniteScroll: z
              .object({
                ownerUser: z.literal('ownerOne'),
                pageSize: z.number().int().positive(),
                minCount: z.number().int().positive(),
                reportIds: z.array(z.string().min(1)),
              })
              .strict(),
            detailPhoto: z
              .object({
                ownerUser: z.literal('ownerOne'),
                reportId: z.string().min(1),
                photoHandle: z.string().min(1),
              })
              .strict(),
            crossOwnerDenied: z
              .object({
                requestingUser: z.literal('ownerOne'),
                ownerUser: z.literal('ownerTwo'),
                reportId: z.string().min(1),
                photoHandle: z.string().min(1),
              })
              .strict(),
            workflowSmoke: z
              .object({
                ownerUser: z.literal('ownerOne'),
                offlineDraftId: z.string().min(1),
              })
              .strict(),
          })
          .strict(),
        backoffice: z
          .object({
            pagination: z
              .object({
                pageSize: z.number().int().positive(),
                minCount: z.number().int().positive(),
                reportIds: z.array(z.string().min(1)),
              })
              .strict(),
            mutationScenarios: z
              .object({
                statusSpeciesLabel: z
                  .object({
                    reportId: z.string().min(1),
                    ownerUser: z.literal('ownerOne'),
                    photoHandle: z.string().min(1),
                    reviewStatus: z.enum(REPORT_REVIEW_STATUSES),
                    species: z.enum(REPORT_SPECIES),
                    labels: z.array(z.string().min(1)),
                  })
                  .strict(),
                rejectionFinalization: z
                  .object({
                    reportId: z.string().min(1),
                    ownerUser: z.literal('ownerTwo'),
                    photoHandle: z.string().min(1),
                    reviewStatus: z.literal('rejected'),
                    species: z.enum(REPORT_SPECIES),
                    labels: z.array(z.string()),
                  })
                  .strict(),
                faceScreeningReview: z
                  .object({
                    reportId: z.string().min(1),
                    ownerUser: z.literal('ownerOne'),
                    photoHandle: z.string().min(1),
                    reviewStatus: z.enum(REPORT_REVIEW_STATUSES),
                    species: z.enum(REPORT_SPECIES),
                    labels: z.array(z.string()),
                  })
                  .strict(),
                faceScreeningFalsePositive: z
                  .object({
                    reportId: z.string().min(1),
                    ownerUser: z.literal('ownerOne'),
                    photoHandle: z.string().min(1),
                    reviewStatus: z.enum(REPORT_REVIEW_STATUSES),
                    species: z.enum(REPORT_SPECIES),
                    labels: z.array(z.string()),
                  })
                  .strict(),
                confirmedGovernance: z
                  .object({
                    reportId: z.string().min(1),
                    ownerUser: z.literal('ownerOne'),
                    photoHandle: z.string().min(1),
                    reviewStatus: z.literal('confirmed'),
                    species: z.enum(REPORT_SPECIES),
                    labels: z.array(z.string().min(1)),
                    confirmationFinalityAtMs: z.number().int().nonnegative(),
                  })
                  .strict(),
                rejectedGovernanceHold: z
                  .object({
                    reportId: z.string().min(1),
                    ownerUser: z.literal('ownerTwo'),
                    photoHandle: z.string().min(1),
                    reviewStatus: z.literal('rejected'),
                    species: z.enum(REPORT_SPECIES),
                    labels: z.array(z.string().min(1)),
                    rejectionFinalityAtMs: z.number().int().nonnegative(),
                    retentionHold: z
                      .object({
                        active: z.literal(true),
                        reason: z.string().min(1),
                        holdUntilMs: z.number().int().nonnegative(),
                      })
                      .strict(),
                  })
                  .strict(),
              })
              .strict(),
            filters: z
              .object({
                reviewStatus: z
                  .object({
                    awaitingReview: SeedBackofficeFilterValueSchema,
                    confirmed: SeedBackofficeFilterValueSchema,
                    rejected: SeedBackofficeFilterValueSchema,
                  })
                  .strict(),
                species: z
                  .object({
                    bee: SeedBackofficeFilterValueSchema,
                    orientalisWasp: SeedBackofficeFilterValueSchema,
                    other: SeedBackofficeFilterValueSchema,
                    unknown: SeedBackofficeFilterValueSchema,
                  })
                  .strict(),
                habitatType: z
                  .object({
                    urbanArea: SeedBackofficeFilterValueSchema,
                    agriculturalArea: SeedBackofficeFilterValueSchema,
                    naturalArea: SeedBackofficeFilterValueSchema,
                    coastalArea: SeedBackofficeFilterValueSchema,
                    other: SeedBackofficeFilterValueSchema,
                  })
                  .strict(),
                observationType: z
                  .object({
                    nest: SeedBackofficeFilterValueSchema,
                    individual: SeedBackofficeFilterValueSchema,
                  })
                  .strict(),
                sightingDate: z
                  .object({
                    onDate: SeedBackofficeFilterValueSchema,
                  })
                  .strict(),
              })
              .strict(),
            detailPhoto: z
              .object({
                reportId: z.string().min(1),
                photoHandle: z.string().min(1),
              })
              .strict(),
            map: z
              .object({
                reportIds: z.array(z.string().min(1)),
                heatmapCluster: BackofficeMapScenarioSchema,
                nationalSpread: BackofficeMapScenarioSchema,
                pointsByReportId: z.record(z.string().min(1), SeedCoordinateSchema),
              })
              .strict(),
            export: z
              .object({
                reportIds: z.array(z.string().min(1)),
              })
              .strict(),
          })
          .strict(),
        retention: z
          .object({
            rejectedPurge: RejectedPurgeSchema,
          })
          .strict(),
      })
      .strict(),
    userAccounts: z
      .object({
        pagination: z
          .object({
            pageSize: z.number().int().positive(),
            minCount: z.number().int().positive(),
            uids: z.array(z.string().min(1)),
          })
          .strict(),
        mutationScenarios: z
          .object({
            disposable: z
              .object({
                uid: z.string().min(1),
                email: z.string().email(),
              })
              .strict(),
          })
          .strict(),
        roleCases: z.record(z.string().min(1), z.string().min(1)),
        projectionCases: z.record(z.string().min(1), z.string().min(1)),
      })
      .strict(),
  })
  .strict();

const ReportTaxonomySchema = z
  .object({
    schemaVersion: z.literal(1),
    species: z.tuple([
      z.literal(REPORT_SPECIES[0]),
      z.literal(REPORT_SPECIES[1]),
      z.literal(REPORT_SPECIES[2]),
      z.literal(REPORT_SPECIES[3]),
    ]),
    habitatTypes: z.tuple([
      z.literal(REPORT_HABITAT_TYPES[0]),
      z.literal(REPORT_HABITAT_TYPES[1]),
      z.literal(REPORT_HABITAT_TYPES[2]),
      z.literal(REPORT_HABITAT_TYPES[3]),
      z.literal(REPORT_HABITAT_TYPES[4]),
    ]),
    observationTypes: z.tuple([
      z.literal(REPORT_OBSERVATION_TYPES[0]),
      z.literal(REPORT_OBSERVATION_TYPES[1]),
    ]),
    reviewStatuses: z.tuple([
      z.literal(REPORT_REVIEW_STATUSES[0]),
      z.literal(REPORT_REVIEW_STATUSES[1]),
      z.literal(REPORT_REVIEW_STATUSES[2]),
    ]),
    presentation: z
      .object({
        flutterAwaitingReviewLabel: z.literal('pending'),
      })
      .strict(),
  })
  .strict();

type FirebasePlatformFixture = z.infer<typeof FirebasePlatformFixtureSchema>;
type SeedContract = z.infer<typeof SeedContractSchema>;
type ReportTaxonomyFixture = z.infer<typeof ReportTaxonomySchema>;

const duplicateMessages = (values: string[], label: string): string[] => {
  const seen = new Set<string>();
  const duplicates = new Set<string>();

  for (const value of values) {
    if (seen.has(value)) {
      duplicates.add(value);
    }

    seen.add(value);
  }

  return [...duplicates].map((value) => `Duplicate ${label}: ${value}`);
};

const collectSeedReportIds = (contract: SeedContract): string[] => [
  ...contract.reports.app.infiniteScroll.reportIds,
  contract.reports.app.detailPhoto.reportId,
  contract.reports.app.crossOwnerDenied.reportId,
  ...contract.reports.backoffice.pagination.reportIds,
  contract.reports.backoffice.detailPhoto.reportId,
  ...contract.reports.backoffice.map.reportIds,
  ...contract.reports.backoffice.export.reportIds,
  ...Object.values(contract.reports.backoffice.mutationScenarios).map(
    (scenario) => scenario.reportId
  ),
  ...Object.values(contract.reports.retention.rejectedPurge.expectedOutcomes).flat(),
];

const validateSeedContractSemantics = (contract: SeedContract): string[] => {
  const errors: string[] = [];

  if (contract.reports.app.infiniteScroll.minCount < 21) {
    errors.push('App infinite scroll scenario must declare at least 21 reports');
  }

  if (contract.reports.backoffice.pagination.minCount < 30) {
    errors.push('Backoffice report pagination must declare at least 30 reports');
  }

  if (contract.userAccounts.pagination.minCount < 12) {
    errors.push('User Account pagination must declare at least 12 accounts');
  }

  errors.push(
    ...duplicateMessages(
      Object.values(contract.users).map((user) => user.uid),
      'seed user uid'
    )
  );
  errors.push(...duplicateMessages(collectSeedReportIds(contract), 'seed report id'));

  const placeKeys = new Set(contract.geography.places.map((place) => place.key));
  const reportIds = new Set(collectSeedReportIds(contract));

  for (const area of ['north', 'center', 'south', 'islands'] as const) {
    if (!contract.geography.requiredSpreadAreas.includes(area)) {
      errors.push(`Seed geography required spread areas must include ${area}`);
    }

    if (!contract.geography.places.some((place) => place.area === area)) {
      errors.push(`Seed geography must declare at least one ${area} place`);
    }
  }

  if (!placeKeys.has(contract.geography.workflowSmokePlaceKey)) {
    errors.push(
      `Seed geography workflow smoke references unknown place ${contract.geography.workflowSmokePlaceKey}`
    );
  }

  for (const [reportId, placeKey] of Object.entries(contract.geography.reportPlaceOverrides)) {
    if (!reportIds.has(reportId)) {
      errors.push(`Seed geography override references unknown report ${reportId}`);
    }

    if (!placeKeys.has(placeKey)) {
      errors.push(`Seed geography override for ${reportId} references unknown place ${placeKey}`);
    }
  }

  const map = contract.reports.backoffice.map;
  const mapReportIds = new Set(map.reportIds);
  const namedMapReportIds = new Set([
    ...map.heatmapCluster.reportIds,
    ...map.nationalSpread.reportIds,
  ]);

  for (const reportId of map.reportIds) {
    if (!namedMapReportIds.has(reportId)) {
      errors.push(`Backoffice map report ${reportId} is not included in a named map scenario`);
    }

    if (!map.pointsByReportId[reportId]) {
      errors.push(`Backoffice map report ${reportId} is missing map coordinates`);
    }
  }

  for (const reportId of Object.keys(map.pointsByReportId)) {
    if (!mapReportIds.has(reportId)) {
      errors.push(`Backoffice map coordinates declared for non-map report ${reportId}`);
    }
  }

  const rejectedPurge = contract.reports.retention.rejectedPurge;
  const malformedFinalityHandle = rejectedPurge.rawMalformedFinality.handle;

  if (
    !rejectedPurge.expectedOutcomes.malformedFinalityMigrationDebt.includes(malformedFinalityHandle)
  ) {
    errors.push(
      `Rejected purge malformed-finality raw handle ${malformedFinalityHandle} must be migration debt`
    );
  }

  if (rejectedPurge.expectedOutcomes.eligible.includes(malformedFinalityHandle)) {
    errors.push(
      `Rejected purge malformed-finality raw handle ${malformedFinalityHandle} must not be eligible`
    );
  }

  return errors;
};

const loadFixture = <T>(schema: z.ZodType<T>, fixture: unknown, surface: string): T => {
  const result = schema.safeParse(fixture);

  if (!result.success) {
    throw new Error(`Invalid ${surface}: ${formatValidationIssues(result.error)}`);
  }

  return result.data;
};

const validateSeedTaxonomy = (
  seedContract: SeedContract,
  reportTaxonomy: ReportTaxonomyFixture
): string[] => {
  const errors: string[] = [];
  const acceptedTaxonomyValues = {
    reviewStatus: new Set(reportTaxonomy.reviewStatuses),
    species: new Set(reportTaxonomy.species),
    habitatType: new Set(reportTaxonomy.habitatTypes),
    observationType: new Set(reportTaxonomy.observationTypes),
  } as const;

  for (const [field, acceptedValues] of Object.entries(acceptedTaxonomyValues) as Array<
    [keyof typeof acceptedTaxonomyValues, Set<string>]
  >) {
    const values = seedContract.reports.backoffice.filters[field];

    for (const [handle, value] of Object.entries(values)) {
      if (!acceptedValues.has(value)) {
        errors.push(`Unsupported taxonomy value for ${field}.${handle}: ${value}`);
      }
    }
  }

  return errors;
};

const validateSeedContractShape = (fixture: unknown): SeedContract =>
  loadFixture(SeedContractSchema, fixture, 'seed contract');

const validateReportTaxonomyShape = (fixture: unknown): ReportTaxonomyFixture =>
  loadFixture(ReportTaxonomySchema, fixture, 'report taxonomy');

const validateFirebasePlatformShape = (fixture: unknown): FirebasePlatformFixture =>
  loadFixture(FirebasePlatformFixtureSchema, fixture, 'firebase platform');

export const firebasePlatformFixture = validateFirebasePlatformShape(rawFirebasePlatformFixture);
export const reportTaxonomyFixture = validateReportTaxonomyShape(rawReportTaxonomyFixture);
export const seedContractFixture = (() => {
  const contract = validateSeedContractShape(rawSeedContractFixture);
  const taxonomyErrors = validateSeedTaxonomy(contract, reportTaxonomyFixture);
  const semanticErrors = validateSeedContractSemantics(contract);

  if (taxonomyErrors.length > 0 || semanticErrors.length > 0) {
    throw new Error(`Invalid seed contract: ${[...semanticErrors, ...taxonomyErrors].join('; ')}`);
  }

  return contract;
})();

export const loadFirebasePlatformFixture = validateFirebasePlatformShape;
export const loadReportTaxonomyFixture = validateReportTaxonomyShape;
export const loadSeedContractFixture = (
  fixture: unknown,
  reportTaxonomy = reportTaxonomyFixture
) => {
  const contract = validateSeedContractShape(fixture);
  const taxonomyErrors = validateSeedTaxonomy(contract, reportTaxonomy);
  const semanticErrors = validateSeedContractSemantics(contract);

  if (taxonomyErrors.length > 0 || semanticErrors.length > 0) {
    throw new Error(`Invalid seed contract: ${[...semanticErrors, ...taxonomyErrors].join('; ')}`);
  }

  return contract;
};
